import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { OrangeSection } from '../../components/ui/OrangeSection'
import { QRScannerModal } from '../../components/admin/QRScannerModal'
import { QRGeneratorModal } from '../../components/admin/QRGeneratorModal'
import PrintButton from '../../components/print/PrintButton'
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  MapPin,
  ArrowLeft,
  RefreshCw,
  Layers,
  Hash,
  Loader2,
  Search,
  QrCode,
  Camera,
  Check,
  LayoutGrid,
  LayoutList,
  Eye
} from 'lucide-react'

interface Shelf {
  id: string
  code: string
  name: string
  description?: string
  shelf_code?: string
  qr_code?: string
  location_id?: string
  location_name?: string
  tiers?: number
  capacity?: number
  active: boolean
  item_count: number
  tote_count: number
  created_at: string
  updated_at: string
}

interface Location {
  id: string
  name: string
}

export default function ShelvesPage() {
  const navigate = useNavigate()

  // Data
  const [shelves, setShelves] = useState<Shelf[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // UI State
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [showQRScanner, setShowQRScanner] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedShelf, setSelectedShelf] = useState<any>(null)
  const [qrTarget, setQrTarget] = useState<'create' | 'edit' | 'search'>('create')

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shelf_code: '',
    qr_code: '',
    location_id: '',
    tiers: 1,
    capacity: 0,
    active: true
  })

  // Fetch shelves
  const fetchShelves = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/shelves-pg')
      const data = await response.json()

      if (data.success) {
        setShelves(data.data)
      } else {
        setError(data.error || 'Failed to fetch shelves')
      }
    } catch (err) {
      setError('Error fetching shelves')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const response = await fetch('/api/locations-pg')
      const data = await response.json()
      if (data.success) {
        setLocations(data.data)
      }
    } catch (err) {
      console.error('Error fetching locations:', err)
    }
  }

  useEffect(() => {
    fetchShelves()
    fetchLocations()
  }, [])

  // Create shelf
  const handleCreate = async () => {
    try {
      const response = await fetch('/api/shelves-pg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        await fetchShelves()
        setShowCreateForm(false)
        resetForm()
      } else {
        alert(data.error || 'Failed to create shelf')
      }
    } catch (err) {
      console.error('Error creating shelf:', err)
      alert('Error creating shelf')
    }
  }

  // Update shelf
  const handleUpdate = async (id: string) => {
    try {
      const response = await fetch(`/api/shelves-pg/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        await fetchShelves()
        setEditingId(null)
        resetForm()
      } else {
        alert(data.error || 'Failed to update shelf')
      }
    } catch (err) {
      console.error('Error updating shelf:', err)
      alert('Error updating shelf')
    }
  }

  // Delete shelf
  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete shelf "${name}"?`)) return

    try {
      const response = await fetch(`/api/shelves-pg/${id}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        await fetchShelves()
      } else {
        alert(data.error || 'Failed to delete shelf')
      }
    } catch (err) {
      console.error('Error deleting shelf:', err)
      alert('Error deleting shelf')
    }
  }

  // Edit shelf
  const handleEdit = (shelf: Shelf) => {
    setFormData({
      name: shelf.name,
      description: shelf.description || '',
      shelf_code: shelf.shelf_code || '',
      qr_code: shelf.qr_code || '',
      location_id: shelf.location_id || '',
      tiers: shelf.tiers || 1,
      capacity: shelf.capacity || 0,
      active: shelf.active
    })
    setEditingId(shelf.id)
    setShowCreateForm(false)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      shelf_code: '',
      qr_code: '',
      location_id: '',
      tiers: 1,
      capacity: 0,
      active: true
    })
  }

  // QR Handlers
  const handleQRScan = (result: string) => {
    if (qrTarget === 'search') {
      setSearchQuery(result)
    } else {
      setFormData({ ...formData, qr_code: result })
    }
    setShowQRScanner(false)
  }

  const handleGenerateCode = (shelf: any) => {
    setSelectedShelf(shelf)
    setShowQRModal(true)
  }

  const handleQRGenerate = async (qrCode: string) => {
    if (!selectedShelf) return

    try {
      const response = await fetch(`/api/shelves-pg/${selectedShelf.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode })
      })

      if (response.ok) {
        fetchShelves()
        setShowQRModal(false)
      }
    } catch (error) {
      console.error('Error saving QR code:', error)
    }
  }

  // Filter shelves
  const filteredShelves = shelves.filter(shelf => {
    const query = searchQuery.toLowerCase()
    return (
      shelf.name.toLowerCase().includes(query) ||
      shelf.shelf_code?.toLowerCase().includes(query) ||
      shelf.qr_code?.toLowerCase().includes(query) ||
      shelf.location_name?.toLowerCase().includes(query)
    )
  })

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              SHELVES MANAGEMENT
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '8px 0 0 0'
            }}>
              MANAGE STORAGE SHELVES AND ORGANIZATION
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(138, 149, 170, 0.1)',
                border: '2px solid rgba(138, 149, 170, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#8A95AA',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              BACK
            </button>
            <button
              onClick={fetchShelves}
              disabled={loading}
              style={{
                background: 'rgba(0, 240, 255, 0.1)',
                border: '2px solid rgba(0, 240, 255, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#00F0FF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                opacity: loading ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <RefreshCw style={{ width: 16, height: 16 }} className={loading ? 'animate-spin' : ''} />
              REFRESH
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                background: 'rgba(107, 207, 127, 0.1)',
                border: '2px solid rgba(107, 207, 127, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#6BCF7F',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <Plus style={{ width: 16, height: 16 }} />
              NEW SHELF
            </button>
          </div>
        </div>
      </div>

      {/* Search & View Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            color: '#8A95AA'
          }} />
          <input
            type="text"
            placeholder="SEARCH BY NAME, CODE, OR QR..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              background: 'rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(0, 240, 255, 0.3)',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              letterSpacing: '1px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.border = '2px solid rgba(0, 240, 255, 0.6)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.2)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.border = '2px solid rgba(0, 240, 255, 0.3)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          />
        </div>
        <button
          onClick={() => { setQrTarget('search'); setShowQRScanner(true); }}
          style={{
            background: 'rgba(185, 27, 255, 0.1)',
            border: '2px solid rgba(185, 27, 255, 0.4)',
            borderRadius: '6px',
            padding: '10px',
            color: '#B91BFF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(185, 27, 255, 0.2)'
            e.currentTarget.style.boxShadow = '0 0 15px rgba(185, 27, 255, 0.3)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(185, 27, 255, 0.1)'
            e.currentTarget.style.boxShadow = 'none'
          }}
        >
          <QrCode style={{ width: 16, height: 16 }} />
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setViewMode('card')}
            style={{
              background: viewMode === 'card' ? 'rgba(0, 240, 255, 0.2)' : 'rgba(138, 149, 170, 0.1)',
              border: viewMode === 'card' ? '2px solid rgba(0, 240, 255, 0.6)' : '2px solid rgba(138, 149, 170, 0.3)',
              borderRadius: '6px',
              padding: '10px',
              color: viewMode === 'card' ? '#00F0FF' : '#8A95AA',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <LayoutGrid style={{ width: 16, height: 16 }} />
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              background: viewMode === 'table' ? 'rgba(0, 240, 255, 0.2)' : 'rgba(138, 149, 170, 0.1)',
              border: viewMode === 'table' ? '2px solid rgba(0, 240, 255, 0.6)' : '2px solid rgba(138, 149, 170, 0.3)',
              borderRadius: '6px',
              padding: '10px',
              color: viewMode === 'table' ? '#00F0FF' : '#8A95AA',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <LayoutList style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="bg-black/60 backdrop-blur-md border-2 border-cyan-500/50 shadow-2xl shadow-cyan-500/30 mb-6">
          <CardHeader className="border-b border-cyan-500/30">
            <CardTitle className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-400">
              Create New Shelf
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white mb-1 block">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder=""
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-white mb-1 block">Location *</label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-content"
                    required
                  >
                    <option value="">Select Location</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-white mb-1 block">Shelf Code</label>
                  <Input
                    value={formData.shelf_code}
                    onChange={(e) => setFormData({ ...formData, shelf_code: e.target.value })}
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="text-sm text-white mb-1 block">Tiers</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.tiers}
                    onChange={(e) => setFormData({ ...formData, tiers: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm text-white mb-1 block">Capacity</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-white mb-1 block">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder=""
                />
              </div>

              <div>
                <label className="text-sm text-white mb-1 block">QR Code</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.qr_code}
                    onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
                    placeholder=""
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { setQrTarget('create'); setShowQRScanner(true); }}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button type="button" onClick={handleCreate}>
                  <Save className="h-4 w-4 mr-2" />
                  Create
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setShowCreateForm(false); resetForm(); }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Shelves List */}
      <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]" />
            <h3 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
              Storage Shelves ({filteredShelves.length})
            </h3>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-400">{error}</div>
          ) : filteredShelves.length === 0 ? (
            <div className="text-center py-12 text-white">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No shelves found. {searchQuery ? 'Try a different search.' : 'Create your first shelf to get started.'}</p>
            </div>
          ) : viewMode === 'card' ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
              gap: '20px'
            }}>
              {filteredShelves.map((shelf) => {
                const shelfColor = '#00F0FF'

                return (
                  <div
                    key={shelf.id}
                    style={{
                      background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
                      border: `2px solid ${shelfColor}`,
                      borderRadius: '12px',
                      padding: '20px',
                      position: 'relative',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      boxShadow: `0 0 20px ${shelfColor}20`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = `0 0 30px ${shelfColor}40`
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = `0 0 20px ${shelfColor}20`
                    }}
                  >
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '20px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '8px',
                          background: `${shelfColor}20`,
                          border: `2px solid ${shelfColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 0 15px ${shelfColor}40`
                        }}>
                          <BookOpen style={{ width: 24, height: 24, color: shelfColor }} />
                        </div>
                        <div>
                          <h3 style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '20px',
                            fontWeight: 700,
                            color: '#E0E6F0',
                            margin: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            textShadow: `0 0 10px ${shelfColor}60`
                          }}>
                            {shelf.name}
                          </h3>
                          {shelf.description && (
                            <p style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '13px',
                              color: '#8A95AA',
                              margin: '4px 0 0 0'
                            }}>
                              {shelf.description}
                            </p>
                          )}
                        </div>
                      </div>
                      {shelf.qr_code ? (
                        <div style={{
                          background: 'rgba(16, 185, 129, 0.2)',
                          border: '1px solid #10B981',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Check style={{ width: 12, height: 12, color: '#10B981' }} />
                          <span style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#10B981',
                            letterSpacing: '1px'
                          }}>QR</span>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(138, 149, 170, 0.1)',
                          border: '1px solid rgba(138, 149, 170, 0.3)',
                          borderRadius: '6px',
                          padding: '4px 8px'
                        }}>
                          <span style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#8A95AA',
                            letterSpacing: '1px'
                          }}>No QR</span>
                        </div>
                      )}
                    </div>

                    {editingId === shelf.id ? (
                      // Edit Mode
                      <div style={{ paddingTop: '16px', borderTop: `1px solid ${shelfColor}30` }}>
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            color: '#8A95AA',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            display: 'block',
                            marginBottom: '4px'
                          }}>NAME</label>
                          <input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              background: 'rgba(0, 0, 0, 0.4)',
                              border: '2px solid rgba(0, 240, 255, 0.3)',
                              borderRadius: '6px',
                              color: '#FFFFFF',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '14px',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            color: '#8A95AA',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            display: 'block',
                            marginBottom: '4px'
                          }}>DESCRIPTION</label>
                          <input
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              background: 'rgba(0, 0, 0, 0.4)',
                              border: '2px solid rgba(0, 240, 255, 0.3)',
                              borderRadius: '6px',
                              color: '#FFFFFF',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '14px',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            color: '#8A95AA',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            display: 'block',
                            marginBottom: '4px'
                          }}>SHELF CODE</label>
                          <input
                            value={formData.shelf_code}
                            onChange={(e) => setFormData({ ...formData, shelf_code: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              background: 'rgba(0, 0, 0, 0.4)',
                              border: '2px solid rgba(0, 240, 255, 0.3)',
                              borderRadius: '6px',
                              color: '#FFFFFF',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '14px',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            color: '#8A95AA',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            display: 'block',
                            marginBottom: '4px'
                          }}>LOCATION</label>
                          <select
                            value={formData.location_id}
                            onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              background: 'rgba(0, 0, 0, 0.4)',
                              border: '2px solid rgba(0, 240, 255, 0.3)',
                              borderRadius: '6px',
                              color: '#FFFFFF',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '14px',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select Location *</option>
                            {locations.map(loc => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                          <div>
                            <label style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '11px',
                              color: '#8A95AA',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              display: 'block',
                              marginBottom: '4px'
                            }}>TIERS</label>
                            <input
                              type="number"
                              value={formData.tiers}
                              onChange={(e) => setFormData({ ...formData, tiers: parseInt(e.target.value) })}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                background: 'rgba(0, 0, 0, 0.4)',
                                border: '2px solid rgba(0, 240, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#FFFFFF',
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '14px',
                                outline: 'none'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '11px',
                              color: '#8A95AA',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              display: 'block',
                              marginBottom: '4px'
                            }}>CAPACITY</label>
                            <input
                              type="number"
                              value={formData.capacity}
                              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                background: 'rgba(0, 0, 0, 0.4)',
                                border: '2px solid rgba(0, 240, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#FFFFFF',
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '14px',
                                outline: 'none'
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            color: '#8A95AA',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.active}
                              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                              style={{ cursor: 'pointer' }}
                            />
                            ACTIVE
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleUpdate(shelf.id)}
                            style={{
                              flex: 1,
                              background: 'rgba(107, 207, 127, 0.1)',
                              border: '2px solid rgba(107, 207, 127, 0.4)',
                              borderRadius: '6px',
                              padding: '10px',
                              color: '#6BCF7F',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '12px',
                              fontWeight: 700,
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <Save style={{ width: 14, height: 14 }} />
                            SAVE
                          </button>
                          <button
                            onClick={() => { setEditingId(null); resetForm(); }}
                            style={{
                              background: 'rgba(138, 149, 170, 0.1)',
                              border: '2px solid rgba(138, 149, 170, 0.4)',
                              borderRadius: '6px',
                              padding: '10px',
                              color: '#8A95AA',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '12px',
                              fontWeight: 700,
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <X style={{ width: 14, height: 14 }} />
                            CANCEL
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <>
                        {/* Stats Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '12px',
                          marginBottom: '16px'
                        }}>
                          {shelf.shelf_code && (
                            <div style={{
                              background: 'rgba(255, 107, 53, 0.1)',
                              border: '1px solid rgba(255, 107, 53, 0.3)',
                              borderRadius: '6px',
                              padding: '8px'
                            }}>
                              <div style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '10px',
                                color: '#8A95AA',
                                marginBottom: '4px',
                                letterSpacing: '1px',
                                textTransform: 'uppercase'
                              }}>SHELF CODE</div>
                              <div style={{
                                fontFamily: 'Rajdhani, monospace',
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#FF6B35'
                              }}>{shelf.shelf_code}</div>
                            </div>
                          )}
                          {shelf.qr_code && (
                            <div style={{
                              background: 'rgba(185, 27, 255, 0.1)',
                              border: '1px solid rgba(185, 27, 255, 0.3)',
                              borderRadius: '6px',
                              padding: '8px'
                            }}>
                              <div style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '10px',
                                color: '#8A95AA',
                                marginBottom: '4px',
                                letterSpacing: '1px',
                                textTransform: 'uppercase'
                              }}>QR CODE</div>
                              <div style={{
                                fontFamily: 'Rajdhani, monospace',
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#B91BFF',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>{shelf.qr_code}</div>
                            </div>
                          )}
                          {shelf.location_name && (
                            <div style={{
                              background: 'rgba(0, 240, 255, 0.1)',
                              border: '1px solid rgba(0, 240, 255, 0.3)',
                              borderRadius: '6px',
                              padding: '8px'
                            }}>
                              <div style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '10px',
                                color: '#8A95AA',
                                marginBottom: '4px',
                                letterSpacing: '1px',
                                textTransform: 'uppercase'
                              }}>LOCATION</div>
                              <div style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#00F0FF'
                              }}>{shelf.location_name}</div>
                            </div>
                          )}
                          <div style={{
                            background: 'rgba(255, 217, 61, 0.1)',
                            border: '1px solid rgba(255, 217, 61, 0.3)',
                            borderRadius: '6px',
                            padding: '8px'
                          }}>
                            <div style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '10px',
                              color: '#8A95AA',
                              marginBottom: '4px',
                              letterSpacing: '1px',
                              textTransform: 'uppercase'
                            }}>TIERS</div>
                            <div style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '18px',
                              fontWeight: 700,
                              color: '#FFD93D'
                            }}>{shelf.tiers || 1}</div>
                          </div>
                          {shelf.capacity ? (
                            <div style={{
                              background: 'rgba(255, 20, 147, 0.1)',
                              border: '1px solid rgba(255, 20, 147, 0.3)',
                              borderRadius: '6px',
                              padding: '8px'
                            }}>
                              <div style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '10px',
                                color: '#8A95AA',
                                marginBottom: '4px',
                                letterSpacing: '1px',
                                textTransform: 'uppercase'
                              }}>CAPACITY</div>
                              <div style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#FF1493'
                              }}>{shelf.capacity}</div>
                            </div>
                          ) : null}
                          <div style={{
                            background: 'rgba(255, 217, 61, 0.1)',
                            border: '1px solid rgba(255, 217, 61, 0.3)',
                            borderRadius: '6px',
                            padding: '8px'
                          }}>
                            <div style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '10px',
                              color: '#8A95AA',
                              marginBottom: '4px',
                              letterSpacing: '1px',
                              textTransform: 'uppercase'
                            }}>ITEMS</div>
                            <div style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '18px',
                              fontWeight: 700,
                              color: '#FFD93D'
                            }}>{shelf.item_count}</div>
                          </div>
                          <div style={{
                            background: 'rgba(107, 207, 127, 0.1)',
                            border: '1px solid rgba(107, 207, 127, 0.3)',
                            borderRadius: '6px',
                            padding: '8px'
                          }}>
                            <div style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '10px',
                              color: '#8A95AA',
                              marginBottom: '4px',
                              letterSpacing: '1px',
                              textTransform: 'uppercase'
                            }}>TOTES</div>
                            <div style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '18px',
                              fontWeight: 700,
                              color: '#6BCF7F'
                            }}>{shelf.tote_count}</div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto auto auto',
                          gap: '8px',
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: `1px solid ${shelfColor}30`
                        }}>
                          <button
                            onClick={() => navigate(`/admin/shelves/${shelf.id}/inventory`)}
                            style={{
                              background: 'rgba(0, 240, 255, 0.1)',
                              border: '2px solid rgba(0, 240, 255, 0.4)',
                              borderRadius: '6px',
                              padding: '10px',
                              color: '#00F0FF',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '12px',
                              fontWeight: 700,
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <Eye style={{ width: 14, height: 14 }} />
                            INVENTORY
                          </button>
                          <button
                            onClick={() => handleEdit(shelf)}
                            style={{
                              background: 'rgba(255, 217, 61, 0.1)',
                              border: '2px solid rgba(255, 217, 61, 0.4)',
                              borderRadius: '6px',
                              padding: '10px',
                              color: '#FFD93D',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(255, 217, 61, 0.2)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 217, 61, 0.3)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255, 217, 61, 0.1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <Edit style={{ width: 16, height: 16 }} />
                          </button>
                          <PrintButton entityType="shelf" entityData={shelf} variant="icon" />
                          <button
                            onClick={() => handleGenerateCode(shelf)}
                            style={{
                              background: 'rgba(185, 27, 255, 0.1)',
                              border: '2px solid rgba(185, 27, 255, 0.4)',
                              borderRadius: '6px',
                              padding: '10px',
                              color: '#B91BFF',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            title="Generate QR Code"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(185, 27, 255, 0.2)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(185, 27, 255, 0.3)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(185, 27, 255, 0.1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <QrCode style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            onClick={() => handleDelete(shelf.id, shelf.name)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '2px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '6px',
                              padding: '10px',
                              color: '#EF4444',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                              e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.3)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <Trash2 style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
      ) : (
        <div className="bg-black/60 backdrop-blur-md border border-cyan-500/30 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-cyan-900/30 border-b border-cyan-500/30">
              <tr>
                <th className="text-left p-3 text-cyan-200">Name</th>
                <th className="text-left p-3 text-cyan-200">Code</th>
                <th className="text-left p-3 text-cyan-200">Location</th>
                <th className="text-left p-3 text-cyan-200">Tiers</th>
                <th className="text-left p-3 text-cyan-200">Items</th>
                <th className="text-left p-3 text-cyan-200">Totes</th>
                <th className="text-left p-3 text-cyan-200">Status</th>
                <th className="text-left p-3 text-cyan-200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShelves.map(shelf => (
                <tr key={shelf.id} className="border-b border-cyan-500/10 hover:bg-cyan-500/10">
                  <td className="p-3 text-cyan-100">{shelf.name}</td>
                  <td className="p-3 text-cyan-300">{shelf.shelf_code || '-'}</td>
                  <td className="p-3 text-cyan-300">{shelf.location_name || '-'}</td>
                  <td className="p-3 text-cyan-300">{shelf.tiers}</td>
                  <td className="p-3 text-cyan-300">{shelf.item_count}</td>
                  <td className="p-3 text-cyan-300">{shelf.tote_count}</td>
                  <td className="p-3">
                    <Badge variant={shelf.active ? 'success' : 'secondary'}>
                      {shelf.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/shelves/${shelf.id}/inventory`)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(shelf)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <PrintButton entityType="shelf" entityData={shelf} variant="icon" />
                      <Button size="sm" variant="ghost" onClick={() => handleGenerateCode(shelf)}>
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(shelf.id, shelf.name)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </div>
      </div>

      {/* QR Scanner Modal */}
      <QRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScan={handleQRScan}
      />

      {/* QR Generator Modal */}
      {showQRModal && selectedShelf && (
        <QRGeneratorModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          onGenerate={handleQRGenerate}
          type="LOCATION"
          id={selectedShelf.id}
          code={selectedShelf.shelf_code}
          title={`Generate QR Code - ${selectedShelf.name}`}
        />
      )}
    </div>
  )
}
